import {
  addDays,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  getQuarter,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subYears,
} from "date-fns";
import type { Maybe } from "../types";

export const relativeDayValues = [
  "$today",
  "$endOfThisMonth",
  "$endOfThisQuarter",
  "$endOfThisYear",
  "$endOfLastMonth",
  "$endOfLastQuarter",
  "$endOfLastYear",
] as const;

export const relativePeriodValues = [
  "$thisMonth",
  "$thisQuarter",
  "$thisYear",
  "$lastMonth",
  "$lastQuarter",
  "$lastYear",
  "$last30Days",
  "$last12Months",
] as const;

export type RelativeDayValue = (typeof relativeDayValues)[number];
export type RelativePeriodValue = (typeof relativePeriodValues)[number];
export type RelativeDateValue = RelativeDayValue | RelativePeriodValue;

const relativeDateLabels: Record<RelativeDateValue, string> = {
  $today: "Today",
  $endOfThisMonth: "End of This Month",
  $endOfThisQuarter: "End of This Quarter",
  $endOfThisYear: "End of This Year",
  $endOfLastMonth: "End of Last Month",
  $endOfLastQuarter: "End of Last Quarter",
  $endOfLastYear: "End of Last Year",
  $thisMonth: "This Month",
  $thisQuarter: "This Quarter",
  $thisYear: "This Year",
  $lastMonth: "Last Month",
  $lastQuarter: "Last Quarter",
  $lastYear: "Last Year",
  $last30Days: "Last 30 Days",
  $last12Months: "Last 12 Months",
};

export const isRelativeDayValue = (value: Maybe<string>): value is RelativeDayValue => {
  return relativeDayValues.some((dayValue) => dayValue === value);
};

export const isRelativePeriodValue = (value: Maybe<string>): value is RelativePeriodValue => {
  return relativePeriodValues.some((periodValue) => periodValue === value);
};

export const isRelativeDateValue = (value: Maybe<string>): value is RelativeDateValue => {
  return isRelativeDayValue(value) || isRelativePeriodValue(value);
};

export const getRelativeDateLabel = (value: RelativeDateValue): string => {
  return relativeDateLabels[value];
};

export const resolveRelativeDay = (value: RelativeDayValue, now: Date = new Date()): Date => {
  switch (value) {
    case "$today":
      return startOfDay(now);
    case "$endOfThisMonth":
      return startOfDay(endOfMonth(now));
    case "$endOfThisQuarter":
      return startOfDay(endOfQuarter(now));
    case "$endOfThisYear":
      return startOfDay(endOfYear(now));
    case "$endOfLastMonth":
      return startOfDay(endOfMonth(subMonths(now, 1)));
    case "$endOfLastQuarter":
      return startOfDay(endOfQuarter(subQuarters(now, 1)));
    case "$endOfLastYear":
      return startOfDay(endOfYear(subYears(now, 1)));
  }
};

export const resolveRelativePeriod = (value: RelativePeriodValue, now: Date = new Date()): [Date, Date] => {
  switch (value) {
    case "$thisMonth":
      return [startOfMonth(now), startOfDay(endOfMonth(now))];
    case "$thisQuarter":
      return [startOfQuarter(now), startOfDay(endOfQuarter(now))];
    case "$thisYear":
      return [startOfYear(now), startOfDay(endOfYear(now))];
    case "$lastMonth":
      return [startOfMonth(subMonths(now, 1)), startOfDay(endOfMonth(subMonths(now, 1)))];
    case "$lastQuarter":
      return [startOfQuarter(subQuarters(now, 1)), startOfDay(endOfQuarter(subQuarters(now, 1)))];
    case "$lastYear":
      return [startOfYear(subYears(now, 1)), startOfDay(endOfYear(subYears(now, 1)))];
    case "$last30Days":
      return [startOfDay(subDays(now, 29)), startOfDay(now)];
    case "$last12Months":
      return [startOfDay(addDays(subMonths(now, 12), 1)), startOfDay(now)];
  }
};

export const getRelativeDatePreview = (value: RelativeDateValue, now: Date = new Date()): string => {
  if (isRelativeDayValue(value)) {
    return format(resolveRelativeDay(value, now), "MMM dd, yyyy");
  }
  const [from, to] = resolveRelativePeriod(value, now);
  switch (value) {
    case "$thisMonth":
    case "$lastMonth":
      return format(from, "MMM yyyy");
    case "$thisQuarter":
    case "$lastQuarter":
      return `Q${getQuarter(from)} ${format(from, "yyyy")}`;
    case "$thisYear":
    case "$lastYear":
      return format(from, "yyyy");
    case "$last30Days":
      return `${format(from, "MMM dd")} - ${format(to, "MMM dd")}`;
    case "$last12Months":
      return `${format(from, "MMM dd, yyyy")} - ${format(to, "MMM dd, yyyy")}`;
  }
};
